import { useState } from 'react';
import { radioReputations, hostReputations, clubReputations, labelConfig, allBenefits, manifestoPillars, reputationGlobalStats, type ReputationData, type ReputationLabel } from '@/mocks/rfwReputation';

type EntityTab = 'radios' | 'locutores' | 'clubes';

const entityTabConfig: Record<EntityTab, { label: string; icon: string; color: string; count: number }> = {
  radios: { label: 'Radios', icon: 'ri-radio-line', color: 'bg-primary-500', count: 12 },
  locutores: { label: 'Locutores', icon: 'ri-mic-line', color: 'bg-secondary-500', count: 5 },
  clubes: { label: 'Clubes', icon: 'ri-vip-crown-line', color: 'bg-accent-500', count: 6 },
};

const labelOrder: ReputationLabel[] = ['nueva', 'activa', 'destacada', 'influyente', 'elite', 'leyenda'];

function getDataForTab(tab: EntityTab): ReputationData[] {
  switch (tab) {
    case 'radios': return radioReputations;
    case 'locutores': return hostReputations;
    case 'clubes': return clubReputations;
    default: return [];
  }
}

export default function ReputacionSection() {
  const [activeTab, setActiveTab] = useState<EntityTab>('radios');
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const data = getDataForTab(activeTab);
  const featured = data.slice(0, 3);
  const rest = data.slice(3, 9);
  const tabConfig = entityTabConfig[activeTab];

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* ─── Section Header ─── */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 border border-amber-200 mb-4">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
            <span className="text-xs font-semibold text-amber-700 tracking-wide uppercase whitespace-nowrap">Sistema de Reputación RFW</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
            La calidad de tu comunidad<br />ahora tiene nombre
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            El RFW Score mide tu <strong className="text-foreground-900">actividad</strong>. La Reputación RFW mide tu{' '}
            <strong className="text-foreground-900">calidad</strong>. Moderación, participación real, retención y crecimiento orgánico. 
            No se compra, se construye.
          </p>
        </div>

        {/* ─── Stats Bar ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-10">
          <div className="bg-background-100 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-foreground-950">{reputationGlobalStats.radiosLeyenda + reputationGlobalStats.radiosElite}</div>
            <div className="text-xs text-foreground-500 mt-1">Radios Elite/Leyenda</div>
          </div>
          <div className="bg-background-100 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-foreground-950">{reputationGlobalStats.locutoresElite}</div>
            <div className="text-xs text-foreground-500 mt-1">Locutores Elite</div>
          </div>
          <div className="bg-background-100 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-foreground-950">{reputationGlobalStats.clubesElite}</div>
            <div className="text-xs text-foreground-500 mt-1">Clubes Elite</div>
          </div>
          <div className="bg-background-100 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-foreground-950">{reputationGlobalStats.entitiesWithSponsors}</div>
            <div className="text-xs text-foreground-500 mt-1">Con Patrocinadores</div>
          </div>
        </div>

        {/* ─── Entity Tabs ─── */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 gap-1">
            {(Object.keys(entityTabConfig) as EntityTab[]).map((tab) => {
              const cfg = entityTabConfig[tab];
              const isActive = activeTab === tab;
              const tabData = getDataForTab(tab);
              return (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setExpandedCard(null); }}
                  className={`relative px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer flex items-center gap-2 ${
                    isActive
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${cfg.icon} text-base`}></i>
                  {cfg.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-foreground-100 text-foreground-600' : 'bg-background-200/70 text-foreground-400'}`}>
                    {tabData.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── Featured Top 3 ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-8">
          {featured.map((entity, idx) => {
            const lbl = labelConfig[entity.label];
            const isFirst = idx === 0;
            return (
              <div
                key={`${entity.entityType}-${entity.entityId}`}
                className={`relative bg-background-50 rounded-2xl border ${lbl.border} p-5 transition-all duration-300 hover:shadow-lg cursor-pointer group ${
                  isFirst ? 'md:scale-105 md:shadow-md ' + lbl.glow : ''
                }`}
                onClick={() => setExpandedCard(expandedCard === entity.entityId ? null : entity.entityId)}
              >
                {/* Trophy badge for #1 */}
                {idx === 0 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                    <i className="ri-vip-crown-2-line"></i> #{entity.rankingPosition}
                  </div>
                )}

                {/* Thumbnail + Label */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-background-100">
                    <img src={entity.thumbnailUrl} alt={entity.entityName} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-foreground-950 text-sm truncate">{entity.entityName}</h4>
                    <p className="text-xs text-foreground-500 truncate">{entity.entitySubtitle}</p>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${lbl.bg} ${lbl.border} flex-shrink-0`}>
                    <span className={`text-xs font-semibold ${lbl.text} whitespace-nowrap`}>{lbl.emoji} {lbl.label}</span>
                  </div>
                </div>

                {/* Reputation Score */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-2xl font-bold text-foreground-950">{entity.reputationScore}</div>
                    <div className="text-xs text-foreground-500">Reputación RFW</div>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${entity.weeklyChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    <i className={`${entity.weeklyChange >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-xs`}></i>
                    {entity.weeklyChange >= 0 ? '+' : ''}{entity.weeklyChange}
                  </div>
                </div>

                {/* Progress to next label */}
                {entity.nextLabel && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-foreground-500 mb-1">
                      <span>{labelConfig[entity.label].emoji} {labelConfig[entity.label].label}</span>
                      <span>{labelConfig[entity.nextLabel].emoji} {labelConfig[entity.nextLabel].label}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${labelConfig[entity.nextLabel].color}`}
                        style={{ width: `${entity.labelProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Expandable Breakdown */}
                {expandedCard === entity.entityId && (
                  <div className="mt-3 pt-3 border-t border-background-200 space-y-2 animate-[fadeIn_0.2s_ease-out]">
                    {entity.breakdown.map((b) => (
                      <div key={b.category}>
                        <div className="flex justify-between items-center mb-0.5">
                          <div className="flex items-center gap-1.5">
                            <i className={`${b.icon} text-xs ${b.percentage >= 70 ? 'text-emerald-500' : b.percentage >= 40 ? 'text-amber-500' : 'text-red-400'}`}></i>
                            <span className="text-xs text-foreground-600">{b.label}</span>
                          </div>
                          <span className="text-xs font-medium text-foreground-800">{b.score}/{b.maxScore}</span>
                        </div>
                        <div className="h-1 rounded-full bg-background-200 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${
                              b.percentage >= 70 ? 'bg-emerald-500' : b.percentage >= 40 ? 'bg-amber-500' : 'bg-red-400'
                            }`}
                            style={{ width: `${b.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button className="mt-3 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1">
                  <i className={`${expandedCard === entity.entityId ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
                  {expandedCard === entity.entityId ? 'Ocultar desglose' : 'Ver desglose'}
                </button>
              </div>
            );
          })}
        </div>

        {/* ─── Rest of the list ─── */}
        {rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {rest.map((entity) => {
              const lbl = labelConfig[entity.label];
              return (
                <div
                  key={`${entity.entityType}-${entity.entityId}`}
                  className="bg-background-50 rounded-xl border border-background-200 p-4 hover:border-background-300 transition-all duration-200 cursor-pointer group"
                  onClick={() => setExpandedCard(expandedCard === entity.entityId ? null : entity.entityId)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={entity.thumbnailUrl} alt={entity.entityName} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground-950 text-sm truncate">{entity.entityName}</h4>
                      <p className="text-xs text-foreground-500 truncate">{entity.entitySubtitle}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold text-foreground-950">{entity.reputationScore}</div>
                      <div className={`flex items-center justify-end gap-1 text-xs font-medium ${entity.weeklyChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        <i className={`${entity.weeklyChange >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>
                        {entity.weeklyChange >= 0 ? '+' : ''}{entity.weeklyChange}
                      </div>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${lbl.bg} ${lbl.border}`}>
                    <span className={`text-xs font-semibold ${lbl.text} whitespace-nowrap`}>{lbl.emoji} {lbl.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══════ LABEL SYSTEM ═══════ */}
        <div className="mb-12">
          <h3 className="text-lg font-bold text-foreground-950 mb-2 text-center">Etiquetas de Reputación</h3>
          <p className="text-sm text-foreground-500 text-center mb-6">Cada entidad recibe una etiqueta basada en su puntuación de reputación. No son permanentes — se ganan y se mantienen.</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {labelOrder.map((label) => {
              const cfg = labelConfig[label];
              return (
                <div key={label} className={`rounded-xl ${cfg.bg} ${cfg.border} p-4 text-center transition-all duration-200 hover:shadow-sm`}>
                  <div className="text-2xl mb-1">{cfg.emoji}</div>
                  <div className={`text-sm font-bold ${cfg.text} mb-1`}>{cfg.label}</div>
                  <div className="text-xs text-foreground-500">{cfg.minScore}–{cfg.maxScore}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════ BENEFITS ═══════ */}
        <div className="mb-12">
          <h3 className="text-lg font-bold text-foreground-950 mb-2 text-center">Beneficios por Reputación</h3>
          <p className="text-sm text-foreground-500 text-center mb-6">A mayor reputación, más beneficios. Cada nivel desbloquea nuevas oportunidades para tu comunidad.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {allBenefits.map((benefit) => {
              const labelCfg = labelConfig[benefit.unlockedAt];
              return (
                <div key={benefit.id} className="bg-background-50 rounded-xl border border-background-200 p-4 hover:border-background-300 transition-all duration-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-9 h-9 rounded-lg ${labelCfg.bg} flex items-center justify-center`}>
                      <i className={`${benefit.icon} ${benefit.color} text-lg`}></i>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${labelCfg.bg} ${labelCfg.text} whitespace-nowrap`}>
                      {labelCfg.emoji} {labelCfg.label}
                    </span>
                  </div>
                  <h4 className="font-semibold text-foreground-950 text-sm mb-1">{benefit.title}</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══════ MANIFESTO ═══════ */}
        <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-foreground-950 mb-2">RFW premia lo que importa</h3>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              No medimos audiencia. Medimos <strong className="text-foreground-900">comunidad</strong>. 
              Estos son los 6 pilares que definen la reputación en RadiofrecuenciaWEB.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {manifestoPillars.map((pillar) => (
              <div key={pillar.id} className="bg-background-50 rounded-xl p-4 border border-background-200">
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${pillar.icon} ${pillar.color} text-xl`}></i>
                  <h4 className="font-bold text-foreground-950 text-sm">{pillar.title}</h4>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ─── CTA ─── */}
        <div className="text-center">
          <p className="text-sm text-foreground-500 mb-3">
            Construí tu reputación. Hacé crecer tu comunidad con calidad.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="/rankings"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bar-chart-line"></i>
              Ver Rankings RFW
            </a>
            <a
              href="/clubes"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-800 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer border border-background-200"
            >
              <i className="ri-vip-crown-line"></i>
              Explorar Clubes
            </a>
            <a
              href="/competencias"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-800 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer border border-background-200"
            >
              <i className="ri-sword-line"></i>
              Ver Competencias
            </a>
          </div>
        </div>

      </div>

      {/* fadeIn animation keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}